import React, {
    FC,
    KeyboardEvent,
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from "react";

import dayjs, { Dayjs } from "dayjs";

import { ChevronLeftIcon, ChevronRightIcon } from "../icons/FieldIcons";

import styles from "./Calendar.module.scss";

/**
 * Extra classes for the calendar's parts, so a field can restyle them from its own stylesheet.
 */
export interface CalendarClasses {
    readonly header?: string;
    readonly weekday?: string;
    readonly weeks?: string;
    readonly day?: string;
    readonly daySelected?: string;
    readonly dayToday?: string;
}

export interface CalendarProps {
    /**
     * The selected day, if any.
     */
    readonly value?: Dayjs | null;
    /**
     * Called with the picked day, at the start of that day in local time.
     */
    readonly onChange: (day: Dayjs) => void;
    /**
     * Whether a day can not be picked. Days before `minDate` and after `maxDate` are
     * already excluded and never reach this.
     */
    readonly shouldDisableDate?: (day: Dayjs) => boolean;
    /**
     * The first day that can be picked. The previous-month arrow stops at its month.
     */
    readonly minDate?: Dayjs;
    /**
     * The last day that can be picked. The next-month arrow stops at its month.
     */
    readonly maxDate?: Dayjs;
    /**
     * The day marked as today. Injectable so a caller that is handed its own "now" marks
     * the same day it judges availability against. Defaults to the real clock.
     */
    readonly today?: Dayjs;
    /**
     * The month to open on when there is no value. Defaults to today's.
     */
    readonly referenceDate?: Dayjs;
    /**
     * Availability is still being fetched: the days are replaced by a skeleton.
     */
    readonly loading?: boolean;
    /**
     * Draw the neighbouring months' days in the leading and trailing weeks instead of
     * leaving those cells blank.
     */
    readonly showDaysOutsideCurrentMonth?: boolean;
    /**
     * Always draw this many weeks, padding with the following month's, so the calendar
     * does not change height between months.
     */
    readonly fixedWeekNumber?: number;
    /**
     * An extra class for a day, on top of the calendar's own.
     */
    readonly getDayClassName?: (day: Dayjs, disabled: boolean) => string | undefined;
    readonly classes?: CalendarClasses;
    readonly className?: string;
}

/**
 * The longest a keyboard search walks looking for a day that can be picked, so a calendar
 * with nothing available can never loop.
 */
const SEARCH_LIMIT_DAYS = 366;

const FULL_DATE_FORMAT = "dddd, MMMM D, YYYY";

function classNames(...names: (string | false | undefined)[]): string {
    return names.filter((name) => !!name).join(" ");
}

/**
 * The weeks drawn for a month, Sunday first, padded with the following month's weeks up
 * to `fixedWeekNumber`.
 */
function weeksOf(month: Dayjs, fixedWeekNumber?: number): Dayjs[][] {
    const first = month.startOf("month");
    const weeksInMonth = Math.ceil((first.day() + first.daysInMonth()) / 7);
    const count = Math.max(weeksInMonth, fixedWeekNumber || 0);
    const start = first.startOf("week");

    return Array.from({ length: count }, (_, week) =>
        Array.from({ length: 7 }, (__, day) => start.add(week * 7 + day, "day"))
    );
}

/**
 * A month grid of days in the ARIA grid pattern, replacing @mui/x-date-pickers' DateCalendar
 * (#1745).
 *
 * One day is in the tab order at a time; the arrow keys move between days, skipping any that
 * can not be picked, Page Up and Page Down move a month, and Home and End go to the start and
 * end of the week. Each day is named by its full date, so a screen reader announces "Monday,
 * August 24, 2026" rather than "24".
 */
export const Calendar: FC<CalendarProps> = (props) => {
    const {
        value,
        onChange,
        shouldDisableDate,
        minDate,
        maxDate,
        referenceDate,
        loading,
        showDaysOutsideCurrentMonth,
        fixedWeekNumber,
        getDayClassName,
        classes = {},
        className,
    } = props;

    const today = useMemo(
        () => (props.today || dayjs()).startOf("day"),
        // Keyed on the day, so a caller handing down a fresh object each render is not a change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.today ? props.today.format("YYYY-MM-DD") : undefined]
    );

    const labelId = useId();
    const gridRef = useRef<HTMLDivElement>(null);
    // Set when the keyboard moves the focused day, so the effect below moves real focus to it.
    const focusOnRender = useRef(false);

    const [month, setMonth] = useState<Dayjs>(() =>
        (value || referenceDate || today).startOf("month")
    );
    const [focusedDay, setFocusedDay] = useState<Dayjs>(() => (value || today).startOf("day"));

    // A value set from outside -- a resumed selection, a preselected date -- brings its month
    // into view, as MUI's calendar did.
    const valueKey = value ? value.format("YYYY-MM-DD") : undefined;
    useEffect(() => {
        if (!value) {
            return;
        }

        setFocusedDay(value.startOf("day"));
        setMonth((current) => (value.isSame(current, "month") ? current : value.startOf("month")));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valueKey]);

    const isOutOfRange = useCallback(
        (day: Dayjs): boolean =>
            (!!minDate && day.isBefore(minDate, "day")) ||
            (!!maxDate && day.isAfter(maxDate, "day")),
        [minDate, maxDate]
    );

    const isDisabled = useCallback(
        (day: Dayjs): boolean =>
            isOutOfRange(day) || (!!shouldDisableDate && shouldDisableDate(day)),
        [isOutOfRange, shouldDisableDate]
    );

    /**
     * The nearest day to `target` between `from` and `to` inclusive that can be picked.
     */
    const closestEnabled = useCallback(
        (target: Dayjs, from: Dayjs, to: Dayjs): Dayjs | undefined => {
            const clamped = target.isBefore(from, "day")
                ? from
                : target.isAfter(to, "day")
                ? to
                : target;
            const span = to.diff(from, "day");

            for (let offset = 0; offset <= span; offset++) {
                for (const candidate of [clamped.add(-offset, "day"), clamped.add(offset, "day")]) {
                    if (
                        !candidate.isBefore(from, "day") &&
                        !candidate.isAfter(to, "day") &&
                        !isDisabled(candidate)
                    ) {
                        return candidate;
                    }
                }
            }

            return undefined;
        },
        [isDisabled]
    );

    // The one day in the tab order: the focused day when it is in view and can be picked,
    // otherwise the nearest one in this month that can.
    const tabbableDay = useMemo(() => {
        if (focusedDay.isSame(month, "month") && !isDisabled(focusedDay)) {
            return focusedDay;
        }

        return closestEnabled(
            focusedDay,
            month.startOf("month"),
            month.endOf("month").startOf("day")
        );
    }, [focusedDay, month, isDisabled, closestEnabled]);

    useEffect(() => {
        if (!focusOnRender.current) {
            return;
        }
        focusOnRender.current = false;

        const timestamp = String(focusedDay.valueOf());
        const target = gridRef.current?.querySelector<HTMLElement>(
            `[data-timestamp="${timestamp}"]`
        );
        target?.focus();
    }, [focusedDay, month]);

    const moveFocus = (day: Dayjs) => {
        focusOnRender.current = true;
        setFocusedDay(day);
        if (!day.isSame(month, "month")) {
            setMonth(day.startOf("month"));
        }
    };

    /** Walk from `day` in steps of `delta` days to the first one that can be picked. */
    const step = (day: Dayjs, delta: number): Dayjs | undefined => {
        let candidate = day;

        for (let walked = 0; walked < SEARCH_LIMIT_DAYS; walked += Math.abs(delta)) {
            candidate = candidate.add(delta, "day");
            if (isOutOfRange(candidate)) {
                return undefined;
            }
            if (!isDisabled(candidate)) {
                return candidate;
            }
        }

        return undefined;
    };

    /** The first day that can be picked walking from `from` towards `to`, inclusive. */
    const scan = (from: Dayjs, to: Dayjs): Dayjs | undefined => {
        const direction = to.isBefore(from, "day") ? -1 : 1;
        const span = Math.abs(to.diff(from, "day"));

        for (let offset = 0; offset <= span; offset++) {
            const candidate = from.add(offset * direction, "day");
            if (!isDisabled(candidate)) {
                return candidate;
            }
        }

        return undefined;
    };

    /** The same day of the month `delta` months away, or the nearest day there that can be picked. */
    const monthJump = (day: Dayjs, delta: number): Dayjs | undefined => {
        const target = day.add(delta, "month");
        return closestEnabled(
            target,
            target.startOf("month"),
            target.endOf("month").startOf("day")
        );
    };

    const onDayKeyDown = (event: KeyboardEvent<HTMLButtonElement>, day: Dayjs) => {
        let next: Dayjs | undefined;

        switch (event.key) {
            case "ArrowLeft":
                next = step(day, -1);
                break;
            case "ArrowRight":
                next = step(day, 1);
                break;
            case "ArrowUp":
                next = step(day, -7);
                break;
            case "ArrowDown":
                next = step(day, 7);
                break;
            case "Home":
            case "End": {
                let edge =
                    event.key === "Home" ? day.startOf("week") : day.endOf("week").startOf("day");
                // With the neighbouring months' days left blank, the week ends at the month's edge.
                if (!showDaysOutsideCurrentMonth && !edge.isSame(day, "month")) {
                    edge =
                        event.key === "Home"
                            ? day.startOf("month")
                            : day.endOf("month").startOf("day");
                }
                next = scan(edge, day);
                break;
            }
            case "PageUp":
                next = monthJump(day, -1);
                break;
            case "PageDown":
                next = monthJump(day, 1);
                break;
            default:
                return;
        }

        event.preventDefault();

        if (next && !next.isSame(day, "day")) {
            moveFocus(next);
        }
    };

    const onDayClick = (day: Dayjs) => {
        setFocusedDay(day);
        if (!day.isSame(month, "month")) {
            setMonth(day.startOf("month"));
        }
        onChange(day);
    };

    const previousDisabled = !!minDate && !month.isAfter(minDate, "month");
    const nextDisabled = !!maxDate && !month.isBefore(maxDate, "month");

    const weeks = useMemo(() => weeksOf(month, fixedWeekNumber), [month, fixedWeekNumber]);
    const weekdays = weeks[0];

    return (
        <div className={classNames(styles.xapp_calendar, className)} data-xapp-calendar="">
            <div className={classNames(styles.xapp_calendar_header, classes.header)}>
                <span aria-live="polite" className={styles.xapp_calendar_label} id={labelId}>
                    {month.format("MMMM YYYY")}
                </span>
                <div className={styles.xapp_calendar_nav}>
                    <button
                        aria-label="Previous month"
                        className={styles.xapp_calendar_nav_button}
                        disabled={previousDisabled}
                        onClick={() => setMonth(month.add(-1, "month"))}
                        type="button"
                    >
                        <ChevronLeftIcon />
                    </button>
                    <button
                        aria-label="Next month"
                        className={styles.xapp_calendar_nav_button}
                        disabled={nextDisabled}
                        onClick={() => setMonth(month.add(1, "month"))}
                        type="button"
                    >
                        <ChevronRightIcon />
                    </button>
                </div>
            </div>

            <div
                aria-busy={loading ? true : undefined}
                aria-labelledby={labelId}
                ref={gridRef}
                role="grid"
            >
                <div className={styles.xapp_calendar_week} role="row">
                    {weekdays.map((day) => (
                        <span
                            aria-label={day.format("dddd")}
                            className={classNames(styles.xapp_calendar_weekday, classes.weekday)}
                            key={day.day()}
                            role="columnheader"
                        >
                            {day.format("dd").charAt(0)}
                        </span>
                    ))}
                </div>

                {loading ? (
                    <div aria-hidden="true" className={styles.xapp_calendar_skeleton}>
                        {weeks.map((week, row) => (
                            <div className={styles.xapp_calendar_week} key={row}>
                                {week.map((day) => (
                                    <span
                                        className={styles.xapp_calendar_skeleton_day}
                                        key={day.valueOf()}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div
                        className={classNames(styles.xapp_calendar_weeks, classes.weeks)}
                        role="rowgroup"
                    >
                        {weeks.map((week, row) => (
                            <div className={styles.xapp_calendar_week} key={row} role="row">
                                {week.map((day) => {
                                    const outside = !day.isSame(month, "month");

                                    if (outside && !showDaysOutsideCurrentMonth) {
                                        return (
                                            <span
                                                className={styles.xapp_calendar_filler}
                                                key={day.valueOf()}
                                                role="gridcell"
                                            />
                                        );
                                    }

                                    const disabled = isDisabled(day);
                                    const selected = !!value && day.isSame(value, "day");
                                    const isToday = day.isSame(today, "day");

                                    return (
                                        <button
                                            aria-current={isToday ? "date" : undefined}
                                            aria-label={day.format(FULL_DATE_FORMAT)}
                                            aria-selected={selected}
                                            className={classNames(
                                                styles.xapp_calendar_day,
                                                classes.day,
                                                selected && styles.xapp_calendar_day_selected,
                                                selected && classes.daySelected,
                                                isToday && styles.xapp_calendar_day_today,
                                                isToday && classes.dayToday,
                                                outside && styles.xapp_calendar_day_outside,
                                                getDayClassName && getDayClassName(day, disabled)
                                            )}
                                            data-timestamp={day.valueOf()}
                                            disabled={disabled}
                                            key={day.valueOf()}
                                            onClick={() => onDayClick(day)}
                                            onKeyDown={(event) => onDayKeyDown(event, day)}
                                            role="gridcell"
                                            tabIndex={
                                                !!tabbableDay && day.isSame(tabbableDay, "day")
                                                    ? 0
                                                    : -1
                                            }
                                            type="button"
                                        >
                                            {day.date()}
                                        </button>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Calendar;
